/**
 * @file billCostWriteBack.js
 * @version 1.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-22
 * @description The bill is the source of truth for a PO's cost (Cam,
 *              2026-10-05): saving a bill puts its unit costs on the PO lines
 *              it bills (server: billService._writeCostsToPos), never its
 *              quantities. When a quantity differs, the save answers with
 *              `quantity_differences`, and this says so for the user to check.
 *              From 2026-10-06 each difference says whether the bill was
 *              compared with what was SUPPLIED (recorded on the PO-D line) or
 *              with what was ordered (`basis`).
 *
 * 1.2.0 (2026-10-08, Cam): "Update PO from bill" is the user's CHOICE. Before
 * a save, poCostChanges lists the PO lines whose cost the bill would change
 * (the same rule as the server's utils/billCostWriteBack); the "Update PO
 * from bill" window ticks them (all unticked on a Queried bill) and the save
 * sends the answer as `po_cost_updates`.
 * 1.3.0 (2026-10-08, review): an order line whose cost differs while the PO
 * already agrees is offered too; on an EDIT a line the user kept before
 * starts unticked and a save that changed no bill cost does not ask
 * (keptBefore / costQuestionNeeded); stillDifferMessage after a save.
 * 1.4.0 (2026-10-13, Jade — "Bill & PO Units Differ"): a bill priced per
 * tonne against a PO line in packs (or the other way round) is read in the
 * PO line's unit — utils/billUnitConversion, the server's own rule: the cost
 * offered is the bag's (£400/T → £10 a 25kg bag), the quantity 40 bags, and
 * each such change carries `unit_conversion` to say so. `itemWeightOf` gives
 * a PO line its item's unit weight; `supplied` what was supplied.
 * 1.4.1 (2026-10-14, Cam — Lacto Production): the bill's lines carry the
 * invoice's unit (`unit`, stored on purchasing.bill_lines), passed to the
 * conversion as evidence — billed per kg is read in packs or tonnes too, and
 * a PO line with no cost by its quantity alone (utils/billUnitConversion 1.1.0).
 * 1.5.0 (2026-10-22, Cam): a PO line being put in its bill's units by this
 * save (utils/billPoEdits `units`) is compared as it will be — in the bill's
 * unit, so no conversion; the PO line's words go to the conversion (a tonne
 * line's pack size), and each change carries `cost_maths` — "£400.00 a tonne
 * = £10.00 a 25kg bag" — for the price cell's tick.
 */

import { formatQuantity } from './numberFormatters';
import { poLineKey } from './billLineReconcile';
import { effectivePoLine } from './billPoEdits';
import { billGroupFigures, billUnitConversion, conversionText, conversionQtyText, conversionCostText } from './billUnitConversion';

const round4 = (n) => Math.round((Number(n) || 0) * 10000) / 10000;
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/** How a PO line is named in the choice: `${poId}|${poLineKey}`. */
export const costKey = (poId, key) => `${Number(poId)}|${key}`;

/** What a PO line's additives add to its unit cost (the server's additiveCostOf). */
export function additiveCostOf(li) {
  const list = Array.isArray(li?.additives) ? li.additives : [];
  return round4(list.reduce((s, a) => s + (num(a?.additivePrice) ?? num(a?.additive_price) ?? 0), 0));
}

/**
 * The bill's unit cost for each PO line it bills — `${poId}|${key}` → cost:
 * one line's price, several lines' average actually paid (the server's rule).
 * @returns {Map<string, { poId: number, key: string, cost: number|null, qty: number, amount: number, lines: object[] }>}
 */
export function billCostsByKey(lines = []) {
  const groups = new Map();
  for (const l of lines || []) {
    const poId = parseInt(l?.purchase_order_id, 10);
    if (!Number.isInteger(poId) || !l.po_line_key) continue;
    const k = costKey(poId, l.po_line_key);
    if (!groups.has(k)) groups.set(k, { poId, key: String(l.po_line_key), lines: [] });
    groups.get(k).lines.push(l);
  }
  // One rule for a group's cost, both sides (utils/billUnitConversion).
  for (const g of groups.values()) {
    const { qty, cost, amount, unit } = billGroupFigures(g.lines);
    g.cost = cost;
    g.qty = round4(qty);
    g.amount = amount;
    g.unit = unit ?? null;
  }
  return groups;
}

/**
 * The PO lines whose cost this bill would change — what the "Update PO from
 * bill" window asks about. The server's rule (utils/billCostWriteBack): bill
 * lines grouped by PO line; never a PO-H, a haulage line, an archived PO or a
 * missing / zero cost. The PO's cost is as it will be after any change typed
 * in its panel. Also a line whose PO cost already IS the bill's but whose
 * ORDER LINE's cost price differs (or is not set) — `order_line_only`
 * (review, 2026-10-08), when the order lines' costs are known.
 *
 * @param {object} args
 * @param {Array<object>} args.lines — the bill's lines (purchase_order_id, po_line_key, quantity, unit_amount)
 * @param {Object<string, object>} args.poById — { [poId]: { purchase_order_number, po_type, status, line_items } }
 * @param {object} [args.poEdits] — { [poId]: { [key]: edit } } (utils/billPoEdits)
 * @param {object} [args.scopes] — { [poId]: scope|null }
 * @param {object} [args.xeroMade] — { [poId]: boolean } — made in Xero (a new cost goes to Xero)
 * @param {object} [args.noSupplied] — { [poId]: boolean } — no supplied quantity here (made in Xero
 *        while the Xero PO sync is on); defaults to xeroMade for an older caller (2026-10-20)
 * @param {object} [args.orderLines] — { [poId]: { [key]: { order_line, cost_price } } } (billing-context lines)
 * @param {(code: string) => any} [args.itemWeightOf] — an item code's unit_weight_kg
 * @param {object} [args.supplied] — { [poId]: { [key]: number|null } } (suppliedFromContext)
 * @returns {Array<object>}
 */
export function poCostChanges({ lines = [], poById = {}, poEdits = {}, scopes = {}, xeroMade = {}, noSupplied = null, orderLines = null, itemWeightOf = null, supplied = null } = {}) {
  const out = [];
  for (const [k, g] of billCostsByKey(lines)) {
    const po = poById?.[g.poId];
    if (!po || po.po_type === 'haulage') continue;
    const st = String(po.status || '').toUpperCase();
    if (st === 'DELETED' || st === 'VOIDED') continue;
    const items = Array.isArray(po.line_items) ? po.line_items : [];
    const index = items.findIndex((li, i) => poLineKey(li, i) === g.key);
    if (index < 0) continue;
    const li = items[index];
    if (li.kind === 'haulage') continue;
    const eff = effectivePoLine(li, poEdits?.[g.poId]?.[g.key], scopes?.[g.poId] || null);
    // Billed per tonne against a PO line in packs (or the other way round):
    // the bill's cost and quantity in the PO line's unit.
    const conv = billUnitConversion({
      billQty: g.qty, billCost: g.cost, billAmount: g.amount, billUnit: g.unit ?? null, poCost: eff.unitAmount,
      poQty: [eff.quantity, (noSupplied || xeroMade)?.[g.poId] ? null
        : (eff.relabelled ? poEdits[g.poId][g.key].units.take.supplied : supplied?.[g.poId]?.[g.key])],
      lineWeight: eff.relabelled ? eff.unitWeightKg : li.unitWeightKg,
      itemWeight: itemWeightOf && li.itemCode ? itemWeightOf(li.itemCode) : null,
      poWords: li.description || null,
    });
    const cost = conv ? conv.cost : g.cost;
    if (cost === null || !(cost > 0)) continue;
    const poMoves = round4(eff.unitAmount) !== round4(cost);
    const ol = orderLines?.[g.poId]?.[g.key];
    const olNew = round4(cost - additiveCostOf(li));
    const olOld = ol && ol.order_line ? (ol.cost_price === null || ol.cost_price === undefined ? null : round4(ol.cost_price)) : undefined;
    const olMoves = olOld !== undefined && olNew >= 0 && olOld !== olNew;
    if (!poMoves && !olMoves) continue;
    out.push({
      key: k,
      purchase_order_id: g.poId,
      purchase_order_number: po.purchase_order_number || null,
      po_line_key: g.key,
      description: li.description || g.lines[0].description || g.lines[0].product_name || null,
      item_code: li.itemCode || null,
      po_cost: round4(eff.unitAmount),
      bill_cost: cost,
      po_cost_moves: poMoves,
      ...(olOld !== undefined ? { order_line_cost: olOld, order_line_cost_new: olNew >= 0 ? olNew : null, order_line_moves: olMoves } : {}),
      order_line_only: !poMoves,
      po_quantity: round4(eff.quantity),
      bill_quantity: conv ? conv.quantity : g.qty,
      ...(conv ? {
        bill_cost_as_billed: g.cost,
        bill_quantity_as_billed: g.qty,
        unit_conversion: { ...conv, cost_text: conversionText(conv), qty_text: conversionQtyText(conv) },
        cost_maths: conversionCostText(conv),
      } : {}),
      xero_made: !!xeroMade?.[g.poId]
    });
  }
  return out;
}

/**
 * What was supplied on each PO line, from Add Bill's billing context
 * ({ [poId]: { lines: { [key]: { supplied } } } }) — for poCostChanges.
 */
export function suppliedFromContext(poContext) {
  const out = {};
  for (const [poId, c] of Object.entries(poContext || {})) {
    out[poId] = {};
    for (const [key, l] of Object.entries(c?.lines || {})) out[poId][key] = l?.supplied ?? null;
  }
  return out;
}

/**
 * The window's starting ticks: every line ticked, unless the bill is Queried
 * — then none (Cam, 2026-10-08: a bill being queried with the supplier must
 * not set the PO's cost by default).
 * @returns {Object<string, boolean>}
 */
export function defaultCostTicks(changes, { queried = false, previousBillCosts = null } = {}) {
  const out = {};
  for (const c of changes || []) {
    const k = c.key || costKey(c.purchase_order_id, c.po_line_key);
    out[k] = !queried && !keptBefore(c, previousBillCosts);
  }
  return out;
}

/**
 * On an EDIT: was this line's bill cost already what it is now when the bill
 * was opened? Then the user decided about it before (kept the PO as it was),
 * so it starts unticked — "Keep" is remembered (review, 2026-10-08).
 * @param {object} change
 * @param {Map<string, { cost }>|null} previousBillCosts — billCostsByKey of the lines as loaded
 */
export function keptBefore(change, previousBillCosts) {
  if (!previousBillCosts) return false;
  const prev = previousBillCosts.get(change.key || costKey(change.purchase_order_id, change.po_line_key));
  // As billed: billCostsByKey's cost is the bill's own, before any unit conversion.
  const billed = change.bill_cost_as_billed ?? change.bill_cost;
  return !!prev && prev.cost !== null && round4(prev.cost) === round4(billed);
}

/**
 * Should a save ask at all? On an edit, only when some line's bill cost
 * changed in this edit — a notes or due-date edit never re-asks about lines
 * the user kept before ("Update PO from this bill…" is there for those).
 */
export function costQuestionNeeded(changes, { previousBillCosts = null } = {}) {
  const list = changes || [];
  if (!list.length) return false;
  if (!previousBillCosts) return true;
  return list.some(c => !keptBefore(c, previousBillCosts));
}

/** After a save: lines that still differ, in words, or null. */
export function stillDifferMessage(costWriteBack) {
  const n = costWriteBack?.cost_differences?.length || 0;
  if (!n) return null;
  return `${n} PO line${n === 1 ? '' : 's'} still differ${n === 1 ? 's' : ''} from this bill on cost (kept as ${n === 1 ? 'it is' : 'they are'}) — use "Update PO from this bill…" in the bill window to change ${n === 1 ? 'it' : 'them'} later.`;
}

/** The answer as the server takes it: every line, ticked or not. */
export function poCostUpdatesPayload(changes, ticks = {}) {
  return (changes || []).map(c => {
    const k = c.key || costKey(c.purchase_order_id, c.po_line_key);
    return { purchase_order_id: Number(c.purchase_order_id), po_line_key: c.po_line_key, update: !!ticks[k] };
  });
}

/** The ticked lines, for "Update PO from this bill…". */
export function chosenCostLines(changes, ticks = {}) {
  return (changes || [])
    .filter(c => ticks[c.key || costKey(c.purchase_order_id, c.po_line_key)])
    .map(c => ({ purchase_order_id: Number(c.purchase_order_id), po_line_key: c.po_line_key }));
}

/**
 * The prompt for quantities that differ between a bill and its POs, or null.
 * Informational: nothing was changed because of them.
 * @param {Array<{ purchase_order_number, description, item_code, bill_quantity, po_quantity }>} diffs
 * @returns {string|null}
 */
export function quantityDifferencesMessage(diffs) {
  const list = Array.isArray(diffs) ? diffs : [];
  if (!list.length) return null;
  const lines = list.map(d => {
    const what = [d.purchase_order_number, d.description || d.item_code].filter(Boolean).join(' · ');
    const against = d.basis === 'supplied' ? 'supplied' : 'PO';
    const billed = d.unit_conversion ? ` (billed as ${d.unit_conversion.qty_text || conversionQtyText(d.unit_conversion)})` : '';
    return `• ${what}: bill ${formatQuantity(d.bill_quantity)}${billed}, ${against} ${formatQuantity(d.po_quantity)}`;
  });
  return [
    `The bill's quantity differs from what was supplied or ordered on ${list.length} line${list.length === 1 ? '' : 's'}:`,
    ...lines,
    '',
    'Nothing has been changed because of this — only the bill\'s unit costs went onto the PO. '
      + 'Check it against the weighbridge tickets or delivery notes, and correct the bill or the order if one of them is wrong.'
  ].join('\n');
}

/**
 * Why "Update PO from this bill…" is greyed on a Search Bills row, or null.
 * The row carries `po_cost_differences` (the server's count; null when it
 * could not be read — then the window is opened and says).
 */
export function poCostRowBlock(bill) {
  if (!bill) return 'No bill';
  const st = String(bill.status || '').toUpperCase();
  if (st === 'VOIDED' || st === 'DELETED') return `This bill is ${st.toLowerCase()}, so it sets no PO's cost`;
  const tt = String(bill.transaction_type || 'BILL').toUpperCase();
  // A supplier credit never changes a PO's cost (Cam, 2026-10-08): it comes
  // after the bill, a one-off amount that its allocation takes off the bill.
  if (tt === 'CREDIT') return 'A supplier credit never changes a PO\'s cost — allocate it to the bill instead';
  if (tt !== 'BILL' || bill.is_credit) return 'Only a bill sets a PO\'s cost — not an overpayment or prepayment';
  if (bill.po_cost_differences === 0) return 'This bill and its POs already agree on cost';
  return null;
}
